import axios from 'axios';
import {
  groupingFiltersURL,
  groupingShortlistURL,
  groupingRunURL,
  groupingQueryURL,
  groupingExportURL,
} from '../../../const';

/** Map header dropdown label to API `data_source` value. */
export function mapDataSourceLabel(label) {
  switch (label) {
    case 'Postgres (GCP)':
      return 'postgres';
    case 'Local':
      return 'local';
    default:
      return 'postgres';
  }
}

function getErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || String(d)).join('; ');
  const message = err?.response?.data?.message;
  if (typeof message === 'string') return message;
  return err?.message || 'Request failed.';
}

function pickFirst(row, keys, fallback = '') {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
}

export function mapHierarchicalRow(row, index) {
  return {
    id: pickFirst(row, ['id'], index),
    parentGroup: pickFirst(row, ['Parent Group', 'parent_group', 'parentGroup']),
    subGroupDescription: pickFirst(row, [
      'Sub-Group Description',
      'sub_group_description',
      'subGroupDescription',
    ]),
    ticketCount: Number(pickFirst(row, ['Ticket Count', 'ticket_count', 'ticketCount'], 0)),
    totalEffortHours: Number(
      pickFirst(row, ['Total Effort (Hours)', 'total_effort_hours', 'totalEffortHours'], 0)
    ),
    rootCause: pickFirst(row, ['Detailed Root Cause', 'detailed_root_cause', 'rootCause']),
    permanentSolution: pickFirst(row, [
      'Permanent Solution / Preventive Action',
      'permanent_solution',
      'permanentSolution',
    ]),
  };
}

export function mapTicketRecord(row, index) {
  const requestId = pickFirst(row, ['Request - ID', 'request_id'], '');
  const internalId = pickFirst(row, ['id'], index);
  return {
    rowKey: requestId ? `${requestId}-${internalId}` : `row-${index}`,
    id: requestId || String(internalId),
    macroArea: pickFirst(row, ['Macro Area (SAP) - MA Area', 'macro_area', 'macroArea']),
    parentGroupId: pickFirst(row, ['Parent_Group_ID', 'parent_group_id', 'parentGroupId']),
    subGroup: pickFirst(row, ['Sub_Group', 'sub_group', 'subGroup']),
    cumulative: Number(
      pickFirst(row, ['Cumilative', 'Cumulative', 'cumulative'], 0)
    ),
    textAnswer: pickFirst(row, ['Request - Text Answer', 'text_answer', 'textAnswer']),
    textRequest: pickFirst(row, ['Request - Text Request', 'text_request', 'textRequest']),
  };
}

export function mapShortlistStats(data) {
  return {
    ticketCount: Number(data?.total_tickets ?? data?.ticket_count ?? 0),
    totalEffortHours: Number(data?.total_hours ?? data?.total_effort_hours ?? 0),
  };
}

export function mapRunResponse(data) {
  const summary = data?.hierarchical_summary ?? data?.hierarchicalSummary ?? [];
  const tickets =
    data?.tagged_tickets ??
    data?.ticket_records ??
    data?.ticketRecords ??
    data?.detailed_tickets ??
    data?.tickets ??
    [];

  return {
    runId: data?.run_id ?? data?.runId ?? '',
    hierarchicalRows: (Array.isArray(summary) ? summary : []).map(mapHierarchicalRow),
    ticketRecords: (Array.isArray(tickets) ? tickets : []).map(mapTicketRecord),
  };
}

export function getQueryResponseText(data) {
  if (typeof data === 'string') return data.trim();
  const text = data?.answer ?? data?.response ?? data?.result ?? data?.payload;
  if (typeof text === 'string' && text.trim()) return text.trim();
  return 'No response received.';
}

export function buildQueryScopes(hierarchicalRows) {
  const groups = new Set();
  hierarchicalRows.forEach((row) => {
    if (row.parentGroup) groups.add(row.parentGroup);
  });
  return ['All Groups & Sub-Groups', ...Array.from(groups)];
}

function buildFilterPayload({ dataSource, module, consultant, fromPeriod, toPeriod, similarityThreshold }) {
  return {
    data_source: mapDataSourceLabel(dataSource),
    module: module || 'All',
    consultant: consultant || 'All',
    from_period: fromPeriod,
    to_period: toPeriod,
    ...(similarityThreshold !== undefined ? { similarity_threshold: similarityThreshold } : {}),
  };
}

export async function fetchGroupingFilters(dataSource) {
  try {
    const { data } = await axios.get(groupingFiltersURL, {
      params: { data_source: mapDataSourceLabel(dataSource) },
      timeout: 120000,
    });
    return {
      modules: data?.modules ?? data?.macro_areas ?? ['All'],
      consultants: data?.consultants ?? ['All'],
      periods: data?.periods ?? data?.from_periods ?? null,
    };
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function createShortlist(filters) {
  try {
    const { data } = await axios.post(
      groupingShortlistURL,
      buildFilterPayload(filters),
      { timeout: 120000 }
    );
    return mapShortlistStats(data);
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function runHierarchicalGrouping(filters) {
  try {
    const { data } = await axios.post(
      groupingRunURL,
      buildFilterPayload(filters),
      { timeout: 300000 }
    );
    return mapRunResponse(data);
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function runCrossGroupQuery({ runId, scope, userQuery }) {
  try {
    const { data } = await axios.post(
      groupingQueryURL,
      {
        run_id: runId,
        scope,
        user_query: userQuery,
      },
      { timeout: 180000 }
    );
    return getQueryResponseText(data);
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function downloadGroupingExport(runId) {
  try {
    const response = await axios.get(`${groupingExportURL}/${runId}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const blob = response.data;
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";\n]+)"?/i);
    const filename = match?.[1] || `hierarchical-analysis-${runId}.xlsx`;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}
